import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';

function QrRooms() {
  const [rooms, setRooms] = useState([]);
  const [roomInput, setRoomInput] = useState('');
  const [savedTargetPhone, setSavedTargetPhone] = useState('');
  const [targetPhoneInput, setTargetPhoneInput] = useState('');
  const [savingNumber, setSavingNumber] = useState(false);
  const [addingRoom, setAddingRoom] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState({ type: '', text: '' });

  const triggerBlobDownload = (blob, filename) => {
    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(blobUrl);
  };

  const downloadIndividualQr = async (roomNo, qrUrl) => {
    try {
      const response = await fetch(qrUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch QR image');
      }
      const blob = await response.blob();
      triggerBlobDownload(blob, `room-${roomNo}-qr.png`);
    } catch (error) {
      console.error('Failed to download QR:', error);
      setNotice({ type: 'error', text: `Failed to download QR for room ${roomNo}` });
    }
  };

  const loadImageFromBlob = (blob) => {
    return new Promise((resolve, reject) => {
      const imageUrl = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(imageUrl);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        reject(new Error('Failed to load image'));
      };
      img.src = imageUrl;
    });
  };

  const downloadAllQrs = async () => {
    try {
      const validCards = roomCards.filter((card) => card.qrUrl);
      if (validCards.length === 0) {
        setNotice({ type: 'error', text: 'No QR codes available to download' });
        return;
      }

      const qrImages = await Promise.all(
        validCards.map(async (card) => {
          const response = await fetch(card.qrUrl);
          if (!response.ok) throw new Error(`Failed to fetch QR for room ${card.roomNo}`);
          const blob = await response.blob();
          const image = await loadImageFromBlob(blob);
          return { roomNo: card.roomNo, image };
        })
      );

      const columns = 3;
      const cardWidth = 360;
      const cardHeight = 360;
      const rows = Math.ceil(qrImages.length / columns);

      const canvas = document.createElement('canvas');
      canvas.width = columns * cardWidth;
      canvas.height = rows * cardHeight;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        setNotice({ type: 'error', text: 'Unable to generate download sheet' });
        return;
      }

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.textAlign = 'center';
      ctx.fillStyle = '#111827';
      ctx.font = 'bold 22px Arial';

      qrImages.forEach((item, index) => {
        const row = Math.floor(index / columns);
        const col = index % columns;
        const baseX = col * cardWidth;
        const baseY = row * cardHeight;

        ctx.fillText(`Room ${item.roomNo}`, baseX + cardWidth / 2, baseY + 36);
        ctx.drawImage(item.image, baseX + 70, baseY + 58, 220, 220);

        ctx.font = '14px Arial';
        ctx.fillStyle = '#4b5563';
        ctx.fillText('Scan to open WhatsApp service menu', baseX + cardWidth / 2, baseY + 310);
        ctx.font = 'bold 22px Arial';
        ctx.fillStyle = '#111827';
      });

      canvas.toBlob((blob) => {
        if (!blob) {
          setNotice({ type: 'error', text: 'Unable to export QR sheet' });
          return;
        }
        triggerBlobDownload(blob, 'all-room-qrs.png');
        setNotice({ type: 'success', text: 'Downloaded all room QR codes' });
      }, 'image/png');
    } catch (error) {
      console.error('Failed to download all QRs:', error);
      setNotice({ type: 'error', text: 'Failed to download all QR codes' });
    }
  };

  const cleanPhone = (value) => String(value || '').replace(/\D/g, '');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [roomsRes, flowRes] = await Promise.all([
        api.get('/api/admin/qr-rooms'),
        api.get('/api/admin/whatsapp-flow-settings')
      ]);

      setRooms(Array.isArray(roomsRes.data) ? roomsRes.data : []);
      const defaultPhone = cleanPhone(
        flowRes?.data?.qr_target_phone ||
        flowRes?.data?.front_office_contact_phone ||
        ''
      );
      setSavedTargetPhone(defaultPhone);
      setTargetPhoneInput(defaultPhone);
    } catch (error) {
      console.error('Failed to load QR rooms:', error);
      setRooms([]);
      setNotice({ type: 'error', text: 'Failed to load QR rooms' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const saveTargetPhone = async () => {
    const phone = cleanPhone(targetPhoneInput);
    if (!phone) {
      setNotice({ type: 'error', text: 'Please enter a valid WhatsApp number before saving' });
      return;
    }

    setSavingNumber(true);
    setNotice({ type: '', text: '' });
    try {
      const flowRes = await api.get('/api/admin/whatsapp-flow-settings');
      const current = flowRes?.data || {};
      const updatedRes = await api.put('/api/admin/whatsapp-flow-settings', {
        ...current,
        qr_target_phone: phone
      });

      const savedPhone = cleanPhone(updatedRes?.data?.qr_target_phone || phone);
      setSavedTargetPhone(savedPhone);
      setTargetPhoneInput(savedPhone);
      setNotice({ type: 'success', text: 'WhatsApp number saved. All QRs now point to this number.' });
    } catch (error) {
      console.error('Failed to save QR WhatsApp number:', error);
      setNotice({ type: 'error', text: 'Failed to save WhatsApp number' });
    } finally {
      setSavingNumber(false);
    }
  };

  const addRoom = async (e) => {
    e.preventDefault();
    const roomNo = String(roomInput || '').trim();
    if (!roomNo) return;

    setAddingRoom(true);
    setNotice({ type: '', text: '' });
    try {
      const response = await api.post('/api/admin/qr-rooms', { room_no: roomNo });
      const data = response.data;
      
      const updatedRooms = Array.isArray(data) ? data : (Array.isArray(data?.rooms) ? data.rooms : []);
      setRooms(updatedRooms);
      setRoomInput('');
      
      if (data && typeof data === 'object' && Array.isArray(data.added)) {
        const addedStr = data.added.join(', ');
        const skippedStr = Array.isArray(data.skipped) && data.skipped.length > 0
          ? ` (skipped duplicates: ${data.skipped.join(', ')})`
          : '';
        setNotice({
          type: 'success',
          text: `Successfully added room(s): ${addedStr}${skippedStr}`
        });
      } else {
        setNotice({ type: 'success', text: 'Room(s) added successfully' });
      }
    } catch (error) {
      console.error('Failed to add room:', error);
      setNotice({ type: 'error', text: error.response?.data?.error || 'Failed to add room' });
    } finally {
      setAddingRoom(false);
    }
  };

  const removeRoom = async (roomNo) => {
    setNotice({ type: '', text: '' });
    try {
      const response = await api.delete(`/api/admin/qr-rooms/${encodeURIComponent(roomNo)}`);
      setRooms(Array.isArray(response.data) ? response.data : []);
      setNotice({ type: 'success', text: `Room ${roomNo} removed` });
    } catch (error) {
      console.error('Failed to delete room:', error);
      setNotice({ type: 'error', text: error.response?.data?.error || 'Failed to delete room' });
    }
  };

  const getPrefilledText = (roomNo) => {
    return `Hey I am at Oleander farms in ${roomNo} room, Can i get list of all the services?`;
  };

  const getWaLink = (roomNo) => {
    const phone = cleanPhone(savedTargetPhone);
    if (!phone) return '';
    const text = encodeURIComponent(getPrefilledText(roomNo));
    return `https://wa.me/${phone}?text=${text}`;
  };

  const roomCards = useMemo(() => {
    return rooms.map((roomNo) => {
      const waLink = getWaLink(roomNo);
      const qrUrl = waLink
        ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(waLink)}`
        : '';

      return { roomNo, waLink, qrUrl };
    });
  }, [rooms, savedTargetPhone]);

  return (
    <Layout>
      <div className="space-y-5 px-2 py-3 sm:px-3 sm:py-4 lg:px-1 lg:py-2">
        <div>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Room QR Management</h1>
              <p className="text-sm text-gray-600 mt-1 max-w-3xl">Create room-wise QR links for WhatsApp. When a guest scans and sends the prefilled message, their number is automatically mapped to that room.</p>
            </div>
            <div className="grid grid-cols-2 gap-3 w-full sm:w-auto">
              <div className="rounded-lg border border-gray-200 px-4 py-3 bg-gray-50 min-w-[130px]">
                <p className="text-xs text-gray-500">Total Rooms</p>
                <p className="text-lg font-semibold text-gray-900">{rooms.length}</p>
              </div>
              <div className="rounded-lg border border-gray-200 px-4 py-3 bg-gray-50 min-w-[130px]">
                <p className="text-xs text-gray-500">QR Number</p>
                <p className="text-sm font-semibold text-gray-900 truncate">{savedTargetPhone || 'Not saved'}</p>
              </div>
            </div>
          </div>
        </div>

        {notice.text && (
          <div className={`rounded-lg border px-4 py-3 text-sm ${notice.type === 'success' ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-700'}`}>
            {notice.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4">
            <div>
              <h2 className="text-base font-semibold text-gray-900">QR Target WhatsApp Number</h2>
              <p className="text-sm text-gray-600 mt-1">Save once to apply this number to all room QR links.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp Number</label>
              <input
                type="text"
                value={targetPhoneInput}
                onChange={(e) => setTargetPhoneInput(cleanPhone(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="919999999999"
              />
              <p className="text-xs text-gray-500 mt-1">Use international format without + (example: 91XXXXXXXXXX).</p>
            </div>

            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-gray-600">Currently saved: <span className="font-medium">{savedTargetPhone || 'Not saved yet'}</span></p>
              <button
                type="button"
                onClick={saveTargetPhone}
                disabled={savingNumber}
                className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-60"
              >
                {savingNumber ? 'Saving...' : 'Save Number'}
              </button>
            </div>
          </div>

          <form onSubmit={addRoom} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Add Room(s)</h2>
              <p className="text-sm text-gray-600 mt-1">Create QR entries. Enter multiple room numbers separated by commas.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Room Number(s)</label>
              <input
                type="text"
                value={roomInput}
                onChange={(e) => setRoomInput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="e.g. 309, 310, 311"
              />
            </div>

            <div className="flex justify-end">
              <button type="submit" disabled={addingRoom} className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-60">
                {addingRoom ? 'Adding...' : 'Add Room(s)'}
              </button>
            </div>
          </form>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Room QR Codes</h2>
              <p className="text-xs text-gray-500 mt-0.5">{roomCards.length} room{roomCards.length === 1 ? '' : 's'} configured</p>
            </div>
            <button
              type="button"
              onClick={downloadAllQrs}
              disabled={roomCards.length === 0}
              className="px-3 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-100"
            >
              Download All
            </button>
          </div>

          {loading ? (
            <p className="text-sm text-gray-600">Loading rooms...</p>
          ) : roomCards.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center bg-gray-50">
              <p className="text-sm text-gray-700">No rooms added yet.</p>
              <p className="text-xs text-gray-500 mt-1">Add a room above to generate its QR code.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {roomCards.map(({ roomNo, waLink, qrUrl }) => (
                <div key={roomNo} className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-base font-semibold text-gray-900">Room {roomNo}</h3>
                    <button
                      type="button"
                      onClick={() => removeRoom(roomNo)}
                      className="text-xs px-2 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50"
                    >
                      Remove
                    </button>
                  </div>

                  {qrUrl ? (
                    <div className="rounded-md bg-white border border-gray-200 p-3">
                      <img src={qrUrl} alt={`QR for room ${roomNo}`} className="w-full max-w-[220px] mx-auto" />
                    </div>
                  ) : (
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Save WhatsApp number above to generate QR.</div>
                  )}

                  <p className="text-xs text-gray-600 break-words">{getPrefilledText(roomNo)}</p>
                  {waLink && (
                    <div className="flex flex-wrap gap-2">
                      <a href={waLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 break-all">
                        Open WhatsApp Link
                      </a>
                      <button
                        type="button"
                        onClick={() => downloadIndividualQr(roomNo, qrUrl)}
                        className="inline-flex items-center justify-center text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
                      >
                        Download
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default QrRooms;
